<script lang="ts">
  /**
   * One ruled line.
   *
   * Fixed height, always — a page's geometry is the notebook's whole premise, so
   * a finished line and an active line occupy identical space. Focusable when
   * active, because `J`/`K` move between active lines only (AF1's actual
   * workflow: you never navigate to something already crossed off).
   *
   * The row's one click routes by state (R2.4 + R2.5): an ACTIVE row takes the
   * keyboard's Space path via `onActivate`, a settled row toggles its stats
   * block, a pending prediction does nothing. Both callbacks are optional (the
   * PageFooter pattern), so a view that passes none — browse — keeps rows
   * inert.
   */
  import type { Task } from '../types/generated';
  import { splitTitleLink, formatDate, formatDay } from '../utils/taskStats';
  import DateMarker from './DateMarker.svelte';

  interface Props {
    task: Task;
    /** Set while the task is still an unacknowledged prediction (§6.3). */
    pending?: boolean;
    /** True on the first line of this task's logical day. */
    showDateMarker?: boolean;
    /** Focused by the `J`/`K` keyboard layer. */
    focused?: boolean;
    /** True when this row just moved to a newly assigned slot. */
    relayout?: boolean;
    /** Number of children spawned by this task, for the `↳` affordance. */
    childCount?: number;
    /**
     * R4.8: the children spawned by a re-entry — their titles and the pages
     * they landed on — for the `↳` arrow's hover tooltip. Presentation only.
     */
    childItems?: { title: string; pageNumber: number | undefined }[];
    /** Archived context: the date marker always shows its year. */
    forceYear?: boolean;
    /**
     * False in read-only views (browse/graveyard): the line is not
     * tab-focusable at all (STEP_8_SPEC.md Review Focus 4). Default true —
     * the notebook view is byte-identical.
     */
    focusable?: boolean;
    /**
     * Browse search: false grey the line out (`line--dim`) — visual only, the
     * text stays in the DOM. Default true (no query, nothing dimmed).
     */
    matched?: boolean;
    /** R2.4: click-an-active-row starts work. Absent → the click does nothing. */
    onActivate?: () => void;
    /** R2.5: click-a-settled-row toggles its stats. Absent → the click does nothing. */
    onToggleStats?: () => void;
    /** Whether this row's stats block is the open one (one per page). */
    statsOpen?: boolean;
    /** Re-entries behind this copy — the `parent_task_id` lineage depth. */
    reentryCount?: number;
  }

  let {
    task,
    pending = false,
    showDateMarker = false,
    focused = false,
    relayout = false,
    childCount = 0,
    childItems = [],
    focusable = true,
    forceYear = false,
    matched = true,
    onActivate,
    onToggleStats,
    statsOpen = false,
    reentryCount = 0,
  }: Props = $props();

  const isActive = $derived(task.state === 'Active');
  const classes = $derived(
    [
      'line',
      `line--${task.state.toLowerCase()}`,
      pending && 'line--pending',
      focused && 'line--focused',
      relayout && 'line--relayout',
      !matched && 'line--dim',
    ]
      .filter(Boolean)
      .join(' '),
  );

  /**
   * The settled row's timestamp — `completed_at` also covers Reentered (the
   * fold stamps it on re-entry); a Dismissed row carries `dismissed_at`.
   */
  const stats = $derived.by(() => {
    if (pending || isActive) return null;
    switch (task.state) {
      case 'Done':
        return { verb: 'Finished', at: task.completed_at };
      case 'Reentered':
        return { verb: 'Re-entered', at: task.completed_at };
      case 'Dismissed':
        return { verb: 'Dismissed', at: task.dismissed_at };
      default:
        return null;
    }
  });

  /** R2.5's hover face: the whole stat set, one line. */
  const summary = $derived.by(() => {
    if (!stats) return undefined;
    const parts = [
      `${stats.verb} ${formatDate(stats.at) || '—'}`,
      `Created ${formatDate(task.created_at)}`,
      `Logical day ${formatDay(task.logical_day)}`,
    ];
    if (reentryCount > 0) {
      parts.push(`${reentryCount} re-entr${reentryCount === 1 ? 'y' : 'ies'}`);
    }
    return parts.join(' · ');
  });

  /**
   * R4.8: the `↳` arrow's hover tooltip — the children a re-entry added, each
   * with the page it landed on. `undefined` (no attribute) when there are none,
   * so a bare arrow keeps the row's own summary tooltip.
   */
  const childSummary = $derived(
    childItems.length === 0
      ? undefined
      : `Children: ${childItems
          .map((c) =>
            c.pageNumber == null ? c.title : `${c.title} (page ${c.pageNumber})`,
          )
          .join(' · ')}`,
  );

  let rowEl: HTMLElement | undefined;
  let statsEl = $state<HTMLElement | undefined>(undefined);

  /** R5.6: a URL in the title, split at render time — presentation only. */
  const link = $derived(splitTitleLink(task.title));

  /**
   * The link is inside the row, whose one click routes by state — without
   * stopping here, following the link would also open the takeover (active
   * row) or toggle its stats (settled one). The navigation itself is native.
   */
  function onLinkClick(event: MouseEvent) {
    event.stopPropagation();
  }

  /**
   * One handler routes every row's click. Pending rows have no assigned slot
   * to work, so they do nothing at all.
   */
  function onRowClick(event: MouseEvent) {
    // Reading the open panel — selecting text, say — is not a toggle.
    if (statsOpen && statsEl?.contains(event.target as Node)) return;
    if (pending) return;
    if (isActive) onActivate?.();
    else onToggleStats?.();
  }

  function onWindowKeydown(event: KeyboardEvent) {
    if (!statsOpen || event.key !== 'Escape') return;
    onToggleStats?.();
  }

  // A press outside the open row closes its stats. The listener exists only
  // while this row's block is open, so the outside-close never fights the
  // toggle of whichever other row was clicked.
  $effect(() => {
    if (!statsOpen) return;
    const onDown = (event: PointerEvent) => {
      if (rowEl && event.target instanceof Node && !rowEl.contains(event.target)) {
        onToggleStats?.();
      }
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  });
</script>

<!--
  `role="option"` rather than `listitem`: the lines are a focusable set driven by
  J/K, which is the listbox pattern. A `listitem` with a non-negative tabindex is
  an ARIA violation, and the role has to say what the element actually IS.
-->
<!--
  The click's keyboard equivalent is the container's own Space/Enter binding on
  `focusedTaskId` — R2.4's click and the keyboard path make the identical
  write — so the handler needs no key event of its own here.
-->
<!-- svelte-ignore a11y_click_events_have_key_events -->
<div
  class={classes}
  data-task-id={task.task_id}
  data-state={task.state}
  data-pending={pending ? 'true' : 'false'}
  data-line={task.line_index}
  tabindex={isActive && focusable ? 0 : -1}
  role="option"
  aria-selected={focused}
  aria-label={`${task.title}${task.state === 'Active' ? '' : `, ${task.state.toLowerCase()}`}`}
  title={summary}
  bind:this={rowEl}
  onclick={onRowClick}
>
  {#if showDateMarker}
    <DateMarker day={task.logical_day} {forceYear} />
  {/if}

  {#if focused}
    <!--
      The margin caret: the notebook's answer to "which line will the next key
      press act on". Focus lives in state, not in the DOM, so nothing else marks
      the line — `aria-hidden` because `aria-selected` already says it.
    -->
    <span class="line-caret" aria-hidden="true">&#8250;</span>
  {/if}

  <div class="line-body">
    <!-- R5.6: a URL in the title collapses to one bracketed link — the href is
         the URL verbatim, the full address rides the title tooltip, and the
         link stays out of the J/K keyboard layer (the row is role="option").
         Single source line: the before/after text is verbatim, so no stray
         whitespace may sneak in around the anchor. -->
    <span class="line-title"
      >{#if link}{link.before}<a
        class="title-link"
        href={link.url}
        title={link.url}
        rel="noopener noreferrer"
        target="_blank"
        tabindex="-1"
        onclick={onLinkClick}>[link]</a
      >{link.after}{:else}{task.title}{/if}</span
    >
  </div>

  {#if task.state === 'Reentered' || childCount > 0}
    <!--
      ↳ in the right margin, surfacing the children of a crossed-off task
      (guarantee 6). Out of flow, so a settled row never shifts its text
      column — one arrow with the count, not two.
    -->
    <span class="line-arrow" aria-hidden="true" title={childSummary}
      >↳{#if childCount > 0}{childCount}{/if}</span
    >
  {/if}

  {#if pending}
    <!-- Display-grade prediction, and it says so. -->
    <span class="badge badge--pending">Pending</span>
  {/if}

  {#if statsOpen && stats}
    <!--
      R2.5: the settled row's stats. Out of flow BELOW the row (like the caret
      and the arrow are out of flow within it) — in flow it would push every
      rule off its line and break the page's fixed geometry. The panel floats
      over the rows beneath it.
    -->
    <!-- ponytail: no flip/clamp positioning — a panel taller than the page
         overflows the card; add viewport clamping if that ever bites. -->
    <div class="line-stats" data-testid="task-stats" bind:this={statsEl}>
      <span>{stats.verb} {formatDate(stats.at) || '—'}</span>
      <span>Created {formatDate(task.created_at)}</span>
      <span>Logical day <DateMarker day={task.logical_day} forceYear /></span>
      {#if reentryCount > 0}
        <span>{reentryCount} re-entr{reentryCount === 1 ? 'y' : 'ies'}</span>
      {/if}
    </div>
  {/if}
</div>

<svelte:window onkeydown={onWindowKeydown} />

<style>
  .line-stats {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 2;
    display: flex;
    flex-wrap: wrap;
    gap: 2px 16px;
    padding: 6px 12px 7px;
    background: var(--paper);
    border: 1px solid color-mix(in srgb, var(--ink-muted) 25%, transparent);
    box-shadow: var(--shadow-panel);
    font-family: var(--font-ui, system-ui);
    font-size: 12px;
    color: var(--ink-muted);
  }
</style>
